/**
 * Firewall API Service
 *
 * Provides methods to interact with the firewall API endpoints for End Customers.
 * Handles VM firewall rules, options, and security groups.
 */

import type {
  FirewallRule,
  FirewallRuleCreate,
  FirewallRuleUpdate,
  FirewallRuleListResponse,
  FirewallRuleReorderRequest,
  FirewallOptions,
  FirewallOptionsUpdate,
  ClusterFirewallStatus,
  SecurityGroup,
  SecurityGroupCreate,
  SecurityGroupUpdate,
  SecurityGroupListResponse,
  SecurityGroupRule,
  SecurityGroupRuleCreate,
  VMSecurityGroup,
  FirewallRuleTemplate,
  FirewallDirection,
  FirewallAction,
  FirewallProtocol,
} from '../types/firewall'
import { apiClient } from './api'

const BASE_URL = '/api/v1/end-customer'

// ============================================
// VM Firewall Rules API
// ============================================

/**
 * List firewall rules for a VM
 * @param vmId - VM instance ID
 * @param sync - Sincroniza com o Proxmox antes de listar (default: true)
 *
 * O default era `false`, com o comentário "to avoid duplicates": a sincronização foi
 * DESLIGADA como paliativo para a duplicação da #802 — o índice do sync chaveava por
 * `proxmox_pos`, toda regra criada pelo portal nascia com `NULL`, e as do Proxmox
 * entravam como novas a cada carregamento. O paliativo tinha preço: regra criada na UI do
 * Proxmox nunca aparecia para o cliente, e o critério "abrir a aba duas vezes não muda a
 * contagem" passava por não sincronizar nada.
 *
 * A causa foi corrigida (o casamento agora é por identidade, e só posição órfã vira regra
 * nova) e está pinada por teste, então a sincronização volta a ser o default — é ela que
 * faz a aba mostrar o firewall REAL da VM, e não só o que passou por aqui.
 */
export const listFirewallRules = async (
  vmId: string,
  sync: boolean = true,
): Promise<FirewallRuleListResponse> => {
  return apiClient.get<FirewallRuleListResponse>(`${BASE_URL}/vms/${vmId}/firewall/rules`, {
    params: { sync },
  })
}

/**
 * Create a new firewall rule for a VM
 */
export const createFirewallRule = async (
  vmId: string,
  rule: FirewallRuleCreate,
): Promise<FirewallRule> => {
  return apiClient.post<FirewallRule>(`${BASE_URL}/vms/${vmId}/firewall/rules`, rule)
}

/**
 * Get a specific firewall rule
 */
export const getFirewallRule = async (vmId: string, ruleId: string): Promise<FirewallRule> => {
  return apiClient.get<FirewallRule>(`${BASE_URL}/vms/${vmId}/firewall/rules/${ruleId}`)
}

/**
 * Update a firewall rule
 */
export const updateFirewallRule = async (
  vmId: string,
  ruleId: string,
  rule: FirewallRuleUpdate,
): Promise<FirewallRule> => {
  return apiClient.put<FirewallRule>(`${BASE_URL}/vms/${vmId}/firewall/rules/${ruleId}`, rule)
}

/**
 * Delete a firewall rule
 */
export const deleteFirewallRule = async (vmId: string, ruleId: string): Promise<void> => {
  await apiClient.delete(`${BASE_URL}/vms/${vmId}/firewall/rules/${ruleId}`)
}

/**
 * Reorder firewall rules
 */
export const reorderFirewallRules = async (
  vmId: string,
  ruleIds: string[],
): Promise<FirewallRule[]> => {
  return apiClient.post<FirewallRule[]>(`${BASE_URL}/vms/${vmId}/firewall/rules/reorder`, {
    rule_ids: ruleIds,
  } as FirewallRuleReorderRequest)
}

/**
 * Sync firewall rules with Proxmox.
 * Uses the list endpoint with sync=true which triggers a Proxmox sync on the backend.
 */
export const syncFirewallRules = async (
  vmId: string,
  _direction: 'from_proxmox' | 'to_proxmox' = 'from_proxmox',
  _overwrite: boolean = false,
): Promise<FirewallRuleListResponse> => {
  return listFirewallRules(vmId, true)
}

// ============================================
// VM Firewall Options API
// ============================================

/**
 * Get firewall options for a VM
 */
export const getFirewallOptions = async (vmId: string): Promise<FirewallOptions> => {
  return apiClient.get<FirewallOptions>(`${BASE_URL}/vms/${vmId}/firewall/options`)
}

/**
 * Whether the firewall of the VM's cluster (datacenter) is enabled (#923).
 */
export const getClusterFirewallStatus = async (vmId: string): Promise<ClusterFirewallStatus> => {
  return apiClient.get<ClusterFirewallStatus>(`${BASE_URL}/vms/${vmId}/firewall/cluster-status`)
}

/**
 * Update firewall options for a VM
 */
export const updateFirewallOptions = async (
  vmId: string,
  options: FirewallOptionsUpdate,
): Promise<FirewallOptions> => {
  return apiClient.put<FirewallOptions>(`${BASE_URL}/vms/${vmId}/firewall/options`, options)
}

/**
 * Enable firewall for a VM
 */
export const enableFirewall = async (
  vmId: string,
): Promise<{ message: string; enabled: boolean }> => {
  return apiClient.post<{ message: string; enabled: boolean }>(
    `${BASE_URL}/vms/${vmId}/firewall/enable`,
  )
}

/**
 * Disable firewall for a VM
 */
export const disableFirewall = async (
  vmId: string,
): Promise<{ message: string; enabled: boolean }> => {
  return apiClient.post<{ message: string; enabled: boolean }>(
    `${BASE_URL}/vms/${vmId}/firewall/disable`,
  )
}

// ============================================
// Security Groups API
// ============================================

/**
 * List all security groups for the organization
 */
export const listSecurityGroups = async (
  includeTemplates: boolean = true,
): Promise<SecurityGroupListResponse> => {
  return apiClient.get<SecurityGroupListResponse>(`${BASE_URL}/security-groups`, {
    params: { include_templates: includeTemplates },
  })
}

/**
 * Create a new security group
 */
export const createSecurityGroup = async (group: SecurityGroupCreate): Promise<SecurityGroup> => {
  return apiClient.post<SecurityGroup>(`${BASE_URL}/security-groups`, group)
}

/**
 * Get a specific security group
 */
export const getSecurityGroup = async (groupId: string): Promise<SecurityGroup> => {
  return apiClient.get<SecurityGroup>(`${BASE_URL}/security-groups/${groupId}`)
}

/**
 * Update a security group
 */
export const updateSecurityGroup = async (
  groupId: string,
  group: SecurityGroupUpdate,
): Promise<SecurityGroup> => {
  return apiClient.put<SecurityGroup>(`${BASE_URL}/security-groups/${groupId}`, group)
}

/**
 * Delete a security group
 */
export const deleteSecurityGroup = async (groupId: string): Promise<void> => {
  await apiClient.delete(`${BASE_URL}/security-groups/${groupId}`)
}

/**
 * List security group templates
 */
export const listSecurityGroupTemplates = async (): Promise<SecurityGroupListResponse> => {
  return apiClient.get<SecurityGroupListResponse>(`${BASE_URL}/security-groups/templates`)
}

// ============================================
// Security Group Rules API
// ============================================

/**
 * List rules for a security group
 */
export const listSecurityGroupRules = async (groupId: string): Promise<SecurityGroupRule[]> => {
  return apiClient.get<SecurityGroupRule[]>(`${BASE_URL}/security-groups/${groupId}/rules`)
}

/**
 * Add a rule to a security group
 */
export const addSecurityGroupRule = async (
  groupId: string,
  rule: SecurityGroupRuleCreate,
): Promise<SecurityGroupRule> => {
  return apiClient.post<SecurityGroupRule>(`${BASE_URL}/security-groups/${groupId}/rules`, rule)
}

/**
 * Delete a rule from a security group
 */
export const deleteSecurityGroupRule = async (groupId: string, ruleId: string): Promise<void> => {
  await apiClient.delete(`${BASE_URL}/security-groups/${groupId}/rules/${ruleId}`)
}

// ============================================
// VM Security Group Associations
// ============================================

/**
 * List security groups attached to a VM
 */
export const listVMSecurityGroups = async (vmId: string): Promise<VMSecurityGroup[]> => {
  return apiClient.get<VMSecurityGroup[]>(`${BASE_URL}/vms/${vmId}/security-groups`)
}

/**
 * Attach a security group to a VM
 */
export const attachSecurityGroup = async (
  vmId: string,
  securityGroupId: string,
): Promise<VMSecurityGroup> => {
  return apiClient.post<VMSecurityGroup>(
    `${BASE_URL}/vms/${vmId}/security-groups/${securityGroupId}/attach`,
  )
}

/**
 * Detach a security group from a VM
 */
export const detachSecurityGroup = async (vmId: string, securityGroupId: string): Promise<void> => {
  await apiClient.delete(`${BASE_URL}/vms/${vmId}/security-groups/${securityGroupId}/detach`)
}

// ============================================
// Helper Functions
// ============================================

/**
 * Get direction display label
 */
export const getDirectionLabel = (direction: FirewallDirection): string => {
  switch (direction) {
    case 'in':
      return 'Entrada'
    case 'out':
      return 'Saída'
    default:
      return direction
  }
}

/**
 * Get direction badge color
 */
export const getDirectionColor = (direction: FirewallDirection): string => {
  switch (direction) {
    case 'in':
      return 'bg-blue-100 text-blue-800'
    case 'out':
      return 'bg-purple-100 text-purple-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

/**
 * Get action display label
 */
export const getActionLabel = (action: FirewallAction): string => {
  switch (action) {
    case 'ACCEPT':
      return 'Permitir'
    case 'DROP':
      return 'Descartar'
    case 'REJECT':
      return 'Rejeitar'
    default:
      return action
  }
}

/**
 * Get action badge color
 */
export const getActionColor = (action: FirewallAction): string => {
  switch (action) {
    case 'ACCEPT':
      return 'bg-green-100 text-green-800'
    case 'DROP':
      return 'bg-red-100 text-red-800'
    case 'REJECT':
      return 'bg-orange-100 text-orange-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

/**
 * Get protocol display label
 */
export const getProtocolLabel = (protocol: FirewallProtocol | null | undefined): string => {
  if (!protocol) return 'Qualquer'
  return protocol.toUpperCase()
}

/**
 * Get enabled status badge color
 */
export const getEnabledColor = (enabled: boolean): string => {
  return enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
}

/**
 * Get enabled status label
 */
export const getEnabledLabel = (enabled: boolean): string => {
  return enabled ? 'Ativo' : 'Inativo'
}

/**
 * Format port for display
 */
export const formatPort = (port: string | null | undefined): string => {
  if (!port) return 'Qualquer'
  return port
}

/**
 * Format IP/CIDR for display
 */
export const formatSource = (source: string | null | undefined): string => {
  if (!source || source.toLowerCase() === 'any') return 'Qualquer'
  return source
}

/**
 * Common firewall rule templates
 */
export const FIREWALL_TEMPLATES: FirewallRuleTemplate[] = [
  {
    name: 'Web Server (HTTP/HTTPS)',
    description: 'Permite tráfego HTTP (80) e HTTPS (443)',
    category: 'web',
    rules: [
      {
        name: 'HTTP',
        comment: 'Allow HTTP traffic',
        direction: 'in',
        action: 'ACCEPT',
        protocol: 'tcp',
        dport: '80',
        enabled: true,
      },
      {
        name: 'HTTPS',
        comment: 'Allow HTTPS traffic',
        direction: 'in',
        action: 'ACCEPT',
        protocol: 'tcp',
        dport: '443',
        enabled: true,
      },
    ],
  },
  {
    name: 'SSH Restrito',
    description: 'Permite SSH apenas de IPs específicos',
    category: 'ssh',
    rules: [
      {
        name: 'SSH',
        comment: 'Allow SSH from trusted networks',
        direction: 'in',
        action: 'ACCEPT',
        protocol: 'tcp',
        dport: '22',
        source: '10.0.0.0/8',
        enabled: true,
      },
    ],
  },
  {
    name: 'Database (PostgreSQL)',
    description: 'Permite conexões PostgreSQL (5432)',
    category: 'database',
    rules: [
      {
        name: 'PostgreSQL',
        comment: 'Allow PostgreSQL connections',
        direction: 'in',
        action: 'ACCEPT',
        protocol: 'tcp',
        dport: '5432',
        enabled: true,
      },
    ],
  },
  {
    name: 'Database (MySQL)',
    description: 'Permite conexões MySQL (3306)',
    category: 'database',
    rules: [
      {
        name: 'MySQL',
        comment: 'Allow MySQL connections',
        direction: 'in',
        action: 'ACCEPT',
        protocol: 'tcp',
        dport: '3306',
        enabled: true,
      },
    ],
  },
  {
    name: 'ICMP (Ping)',
    description: 'Permite ping/ICMP',
    category: 'custom',
    rules: [
      {
        name: 'ICMP',
        comment: 'Allow ICMP (ping)',
        direction: 'in',
        action: 'ACCEPT',
        protocol: 'icmp',
        enabled: true,
      },
    ],
  },
]

/**
 * Default firewall API export
 */
export const firewallApi = {
  // Rules
  listFirewallRules,
  createFirewallRule,
  getFirewallRule,
  updateFirewallRule,
  deleteFirewallRule,
  reorderFirewallRules,
  syncFirewallRules,
  // Options
  getFirewallOptions,
  getClusterFirewallStatus,
  updateFirewallOptions,
  enableFirewall,
  disableFirewall,
  // Security Groups
  listSecurityGroups,
  createSecurityGroup,
  getSecurityGroup,
  updateSecurityGroup,
  deleteSecurityGroup,
  listSecurityGroupTemplates,
  // Security Group Rules
  listSecurityGroupRules,
  addSecurityGroupRule,
  deleteSecurityGroupRule,
  // VM Security Groups
  listVMSecurityGroups,
  attachSecurityGroup,
  detachSecurityGroup,
}

export default firewallApi
